import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import type { AssignedExpenseInvoice, BookingExpenseInvoiceMappingDetailItem } from '../../Shared/Types/bookingTypes';
import { formatBookingCurrency } from '../Helpers/formatBookingCurrency';
import { useGetBookingExpenseInvoiceTagsLookupQuery } from '../Service/bookingExpenseInvoiceTagsLookupApi';

interface BookingAssignedExpenseInvoicesTableProps {
  expenseInvoices: AssignedExpenseInvoice[];
  isLoading?: boolean;
  editable?: boolean;
  onRowChange?: (item: BookingExpenseInvoiceMappingDetailItem) => void;
  onUnassign?: (expenseInvoiceId: number) => void;
  pendingUnassignExpenseInvoiceId?: number | null;
}

type PaymentMethodValue = 'cash' | 'transfer' | null;

interface RowEditState {
  paymentMethod: PaymentMethodValue;
  tagId: number | null;
}

const PAYMENT_METHOD_OPTIONS: { value: PaymentMethodValue; label: string }[] = [
  { value: null, label: '—' },
  { value: 'cash', label: 'Gotówka' },
  { value: 'transfer', label: 'Przelew' },
];

const resolvePaymentMethodLabel = (value: PaymentMethodValue): string => {
  switch (value) {
    case 'cash': return 'Gotówka';
    case 'transfer': return 'Przelew';
    default: return '—';
  }
};

const normalizeString = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
};

const resolveDocumentReference = (invoice: AssignedExpenseInvoice): string =>
  normalizeString(invoice.documentReference) ??
  normalizeString(invoice.invoiceNumber) ??
  `#${invoice.expenseInvoiceId}`;

const resolveDisplayIssuerName = (invoice: AssignedExpenseInvoice): string =>
  normalizeString(invoice.displayIssuerName) ??
  normalizeString(invoice.sellerName) ??
  'Koszt uproszczony';

const resolveAssignmentTagName = (invoice: AssignedExpenseInvoice): string =>
  normalizeString(invoice.assignmentTagName) ?? '—';

const resolveDocumentKindLabel = (documentKind: string | null | undefined): string => {
  const normalizedKind = String(documentKind ?? '').trim().toLowerCase();

  if (normalizedKind === 'simplifiedcost' || normalizedKind === 'simplified_cost') {
    return 'Koszt uproszczony';
  }

  return 'Faktura';
};

const resolveDocumentKindChipColor = (
  documentKind: string | null | undefined
): 'primary' | 'secondary' => {
  const normalizedKind = String(documentKind ?? '').trim().toLowerCase();

  return normalizedKind === 'simplifiedcost' || normalizedKind === 'simplified_cost'
    ? 'secondary'
    : 'primary';
};

const BookingAssignedExpenseInvoicesTable: React.FC<BookingAssignedExpenseInvoicesTableProps> = ({
  expenseInvoices,
  isLoading,
  editable = false,
  onRowChange,
  onUnassign,
  pendingUnassignExpenseInvoiceId = null,
}) => {
  const [rowEdits, setRowEdits] = useState<Record<number, RowEditState>>({});
  const [confirmingUnassignId, setConfirmingUnassignId] = useState<number | null>(null);
  const { data: availableTags = [] } = useGetBookingExpenseInvoiceTagsLookupQuery({
    skip: !editable,
  });

  const handleRequestUnassign = useCallback((expenseInvoiceId: number) => {
    setConfirmingUnassignId(expenseInvoiceId);
  }, []);

  const handleCancelUnassign = useCallback(() => {
    setConfirmingUnassignId(null);
  }, []);

  const handleConfirmUnassign = useCallback(
    (expenseInvoiceId: number) => {
      setConfirmingUnassignId(null);
      onUnassign?.(expenseInvoiceId);
    },
    [onUnassign]
  );

  useEffect(() => {
    if (!editable) {
      return;
    }

    const initial: Record<number, RowEditState> = {};
    expenseInvoices.forEach((inv) => {
      initial[inv.expenseInvoiceId] = {
        paymentMethod: (inv.paymentMethod as PaymentMethodValue) ?? null,
        tagId: inv.assignmentTagId ?? null,
      };
    });
    setRowEdits(initial);
  }, [expenseInvoices, editable]);

  const handlePaymentMethodChange = useCallback(
    (invoice: AssignedExpenseInvoice, paymentMethod: PaymentMethodValue) => {
      setRowEdits((prev) => {
        const current = prev[invoice.expenseInvoiceId] ?? { paymentMethod: null, tagId: invoice.assignmentTagId ?? null };
        return { ...prev, [invoice.expenseInvoiceId]: { ...current, paymentMethod } };
      });
      const currentTagId = rowEdits[invoice.expenseInvoiceId]?.tagId ?? invoice.assignmentTagId ?? null;
      onRowChange?.({ expenseInvoiceId: invoice.expenseInvoiceId, paymentMethod, tagId: currentTagId });
    },
    [onRowChange, rowEdits]
  );

  const handleTagChange = useCallback(
    (invoice: AssignedExpenseInvoice, tagId: number | null) => {
      setRowEdits((prev) => {
        const current = prev[invoice.expenseInvoiceId] ?? { paymentMethod: (invoice.paymentMethod as PaymentMethodValue) ?? null, tagId: null };
        return { ...prev, [invoice.expenseInvoiceId]: { ...current, tagId } };
      });
      const currentPaymentMethod = rowEdits[invoice.expenseInvoiceId]?.paymentMethod ?? (invoice.paymentMethod as PaymentMethodValue) ?? null;
      onRowChange?.({ expenseInvoiceId: invoice.expenseInvoiceId, paymentMethod: currentPaymentMethod, tagId });
    },
    [onRowChange, rowEdits]
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Ładowanie przypisanych dokumentów kosztowych...</Typography>
      </Box>
    );
  }

  if (expenseInvoices.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          Brak przypisanych dokumentów kosztowych do tego bookingu
        </Typography>
      </Box>
    );
  }

  const totalAllocatedPln = expenseInvoices.reduce((sum, invoice) => {
    const c = invoice.currency?.trim().toUpperCase();
    const isPln = !c || c === '' || c === 'PLN';
    return sum + (isPln || invoice.currencyRate == null
      ? invoice.allocatedAmount
      : invoice.allocatedAmount * invoice.currencyRate);
  }, 0);

  const showCurrencyRate = expenseInvoices.some((inv) => {
    const c = inv.currency?.trim().toUpperCase();
    return (c != null && c !== '' && c !== 'PLN') || inv.currencyRate != null;
  });

  return (
    <Box>
      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Dokument</strong>
              </TableCell>
              <TableCell>
                <strong>Wystawca</strong>
              </TableCell>
              <TableCell>
                <strong>Opis / tagi</strong>
              </TableCell>
              <TableCell>
                <strong>Rodzaj wydatku</strong>
              </TableCell>
              <TableCell>
                <strong>Forma płatności</strong>
              </TableCell>
              <TableCell>
                <strong>Data wystawienia</strong>
              </TableCell>
              {showCurrencyRate && (
                <TableCell>
                  <strong>Waluta / Kurs</strong>
                </TableCell>
              )}
              <TableCell align="right">
                <strong>Wartość brutto</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Kwota przypisana</strong>
              </TableCell>
              <TableCell align="right">
                <strong>% alokacji</strong>
              </TableCell>
              {onUnassign && (
                <TableCell align="right">
                  <strong>Akcje</strong>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {expenseInvoices.map((invoice) => {
              const allocationPercent =
                invoice.totalGross !== 0
                  ? ((invoice.allocatedAmount / invoice.totalGross) * 100).toFixed(1)
                  : '0.0';
              const documentReference = resolveDocumentReference(invoice);
              const displayIssuerName = resolveDisplayIssuerName(invoice);
              const description = normalizeString(invoice.description);
              const tags = (invoice.tags ?? [])
                .map((tag) => {
                  const normalizedTagName = normalizeString(tag?.name);

                  if (typeof tag?.id !== 'number' || !Number.isFinite(tag.id) || !normalizedTagName) {
                    return null;
                  }

                  return {
                    id: tag.id,
                    name: normalizedTagName,
                  };
                })
                .filter((tag): tag is { id: number; name: string } => tag !== null);

              const rowEditState = rowEdits[invoice.expenseInvoiceId];
              const normalizedCurrency = invoice.currency?.trim().toUpperCase() ?? null;
              const isForeignCurrency =
                (normalizedCurrency != null && normalizedCurrency !== '' && normalizedCurrency !== 'PLN') ||
                invoice.currencyRate != null;

              return (
                <TableRow key={invoice.expenseInvoiceId} hover>
                  <TableCell>
                    <Stack spacing={1}>
                      <Chip
                        label={resolveDocumentKindLabel(invoice.documentKind)}
                        size="small"
                        color={resolveDocumentKindChipColor(invoice.documentKind)}
                        variant="outlined"
                        sx={{ alignSelf: 'flex-start' }}
                      />
                      <Typography variant="body2" fontWeight="medium">
                        {documentReference}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{displayIssuerName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <Typography variant="body2" color={description ? 'text.primary' : 'text.secondary'}>
                        {description ?? 'Brak opisu'}
                      </Typography>
                      {tags.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {tags.map((tag) => (
                            <Chip key={tag.id} label={tag.name} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <Select
                        size="small"
                        value={rowEditState?.tagId ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleTagChange(invoice, typeof val === 'number' ? val : null);
                        }}
                        displayEmpty
                        variant="outlined"
                        sx={{ minWidth: 120 }}
                        inputProps={{ 'aria-label': 'Rodzaj wydatku' }}
                      >
                        <MenuItem value=""><em>Brak</em></MenuItem>
                        {availableTags.map((tag) => (
                          <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography variant="body2">{resolveAssignmentTagName(invoice)}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editable ? (
                      <Select
                        size="small"
                        value={rowEditState?.paymentMethod ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          handlePaymentMethodChange(
                            invoice,
                            (val === 'cash' || val === 'transfer') ? val : null
                          );
                        }}
                        displayEmpty
                        variant="outlined"
                        sx={{ minWidth: 120 }}
                        inputProps={{ 'aria-label': 'Forma płatności' }}
                      >
                        {PAYMENT_METHOD_OPTIONS.map((opt) => (
                          <MenuItem key={String(opt.value)} value={opt.value ?? ''}>
                            {opt.value === null ? <em>{opt.label}</em> : opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography
                        variant="body2"
                        color={invoice.paymentMethod ? 'text.primary' : 'text.secondary'}
                      >
                        {resolvePaymentMethodLabel((invoice.paymentMethod as PaymentMethodValue) ?? null)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {invoice.issueDate ? dayjs(invoice.issueDate).format('DD.MM.YYYY') : '—'}
                    </Typography>
                  </TableCell>
                  {showCurrencyRate && (
                    <TableCell>
                      <Typography variant="body2" color={isForeignCurrency ? 'text.primary' : 'text.secondary'}>
                        {invoice.currency ?? '—'}{' '}
                        {invoice.currencyRate != null ? invoice.currencyRate.toFixed(4) : '—'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {invoice.totalGross.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                      {invoice.currency ?? 'PLN'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium" color="primary">
                      {formatBookingCurrency(
                        isForeignCurrency && invoice.currencyRate != null
                          ? invoice.allocatedAmount * invoice.currencyRate
                          : invoice.allocatedAmount
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${allocationPercent}%`}
                      size="small"
                      color={parseFloat(allocationPercent) === 100 ? 'success' : 'default'}
                    />
                  </TableCell>
                  {onUnassign && (
                    <TableCell align="right">
                      {(() => {
                        const isPending = pendingUnassignExpenseInvoiceId === invoice.expenseInvoiceId;
                        const isConfirming = confirmingUnassignId === invoice.expenseInvoiceId;

                        if (isPending) {
                          return (
                            <Button size="small" color="error" disabled>
                              Odpinanie...
                            </Button>
                          );
                        }

                        if (isConfirming) {
                          return (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                Na pewno odpiąć?
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                variant="contained"
                                onClick={() => handleConfirmUnassign(invoice.expenseInvoiceId)}
                              >
                                Tak, odepnij
                              </Button>
                              <Button size="small" onClick={handleCancelUnassign}>
                                Anuluj
                              </Button>
                            </Stack>
                          );
                        }

                        return (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={pendingUnassignExpenseInvoiceId != null}
                            onClick={() => handleRequestUnassign(invoice.expenseInvoiceId)}
                          >
                            Odepnij
                          </Button>
                        );
                      })()}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Typography variant="body1">
          <strong>Razem przypisano:</strong>{' '}
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {formatBookingCurrency(totalAllocatedPln)}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default BookingAssignedExpenseInvoicesTable;
